import axios from 'axios'

/**
 * HttpClient wrapper around axios that matches the @microbadevs/http-client API
 */
class HttpClient {
  constructor(
    baseURL,
    username = null,
    password = null,
    accessToken = null,
    refreshToken = null,
    useBasicAuth = false,
    isExternalRequest = false,
    isExternalClientRequest = false
  ) {
    // Create axios instance with base configuration
    this.client = axios.create({
      baseURL: baseURL || ''
    })

    // Store tokens for potential refresh logic
    this.accessToken = accessToken
    this.refreshToken = refreshToken
    this.isExternalClientRequest = isExternalClientRequest

    // Configure authentication
    if (useBasicAuth && username && password) {
      // Use basic auth
      this.client.defaults.auth = {
        username,
        password
      }
    } else if (accessToken) {
      // Use bearer token auth
      this.client.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`
    }

    // Add request interceptor for external client requests
    if (isExternalClientRequest) {
      this.client.interceptors.request.use(
        (config) => {
          // Add any specific headers or config for external requests
          return config
        },
        (error) => {
          return Promise.reject(error)
        }
      )
    }

    // Make the instance callable as a function
    const callableInstance = this._createCallableInstance()

    // Copy all properties and methods to the callable instance
    Object.setPrototypeOf(callableInstance, HttpClient.prototype)
    Object.assign(callableInstance, this)

    return callableInstance
  }

  /**
   * Creates a callable function that can be invoked like: client(config) or client(method, url, data)
   */
  _createCallableInstance() {
    const instance = (configOrMethod, url, data) => {
      if (typeof configOrMethod === 'object') {
        // Called with config object: client({ method, url, data, responseType })
        return this.client.request(configOrMethod)
      } else {
        // Called with separate arguments: client('GET', '/path', data)
        return this.client.request({
          method: configOrMethod,
          url: url,
          data: data
        })
      }
    }

    return instance
  }

  /**
   * HTTP GET request
   */
  get(url, config) {
    return this.client.get(url, config)
  }

  /**
   * HTTP POST request
   */
  post(url, data, config) {
    return this.client.post(url, data, config)
  }

  /**
   * HTTP PUT request
   */
  put(url, data, config) {
    return this.client.put(url, data, config)
  }

  /**
   * HTTP DELETE request
   */
  delete(url, config) {
    return this.client.delete(url, config)
  }

  /**
   * HTTP PATCH request
   */
  patch(url, data, config) {
    return this.client.patch(url, data, config)
  }

  /**
   * HTTP HEAD request
   */
  head(url, config) {
    return this.client.head(url, config)
  }

  /**
   * HTTP OPTIONS request
   */
  options(url, config) {
    return this.client.options(url, config)
  }

  /**
   * Generic request method
   */
  request(config) {
    return this.client.request(config)
  }

  /**
   * Get interceptors (proxy to axios client interceptors)
   */
  get interceptors() {
    return this.client.interceptors
  }

  /**
   * Get defaults (proxy to axios client defaults)
   */
  get defaults() {
    return this.client.defaults
  }
}

export default HttpClient
